import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CurrencyDollar, TrendUp, Percent } from "@phosphor-icons/react";

/**
 * ProfitSimulator — "¿Cuánto habrías ganado TÚ?"
 * Simulador interactivo: el visitante mueve cuánto apostaría por jugada
 * y ve su ganancia en dólares con el récord REAL de la plataforma.
 */
export default function ProfitSimulator({ totalPicks = 38, netUnits = 2.1 }) {
  const { t } = useTranslation();
  const [betSize, setBetSize] = useState(25);

  const calc = useMemo(() => {
    const wagered = totalPicks * betSize;
    const profit = netUnits * betSize;
    const roi = totalPicks > 0 ? (netUnits / totalPicks) * 100 : 0;
    return { wagered, profit, roi };
  }, [betSize, totalPicks, netUnits]);

  const fmt = (n) => n.toLocaleString("es", { maximumFractionDigits: 0 });

  const marks = [10, 25, 50, 100, 250];

  return (
    <section className="py-12 px-4" style={{ background: "linear-gradient(180deg, #0A0A14 0%, #0E0E1E 100%)" }} data-testid="profit-simulator">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
            style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)" }}>
            <CurrencyDollar weight="fill" size={14} style={{ color: "#10B981" }} />
            <span className="text-[10px] font-black tracking-widest uppercase" style={{ color: "#10B981" }}>
              {t("landing.sim_badge", "Simulador")}
            </span>
          </div>
          <h2 className="text-2xl font-black text-white tracking-tight" style={{ letterSpacing: "-0.02em" }}>
            {t("landing.sim_title", "¿Cuánto habrías ganado")} <span className="text-gradient-gold">{t("landing.sim_title_you", "tú")}?</span>
          </h2>
          <p className="text-sm mt-2" style={{ color: "#9090B0" }}>
            {t("landing.sim_sub", "Mueve la barra con lo que apuestas por jugada. Cálculos con nuestro récord real.")}
          </p>
        </div>

        <div className="rounded-3xl p-6 relative overflow-hidden"
          style={{
            background: "linear-gradient(160deg, #151524 0%, #101018 100%)",
            border: "1px solid rgba(212,175,55,0.22)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}>
          {/* Resplandor superior */}
          <div style={{
            position: "absolute", top: 0, left: "20%", right: "20%", height: 2,
            background: "linear-gradient(90deg, transparent, #D4AF37, transparent)",
            boxShadow: "0 0 20px rgba(212,175,55,0.6)",
          }} />

          {/* Ganancia protagonista */}
          <div className="text-center mb-6">
            <div className="text-[11px] font-black tracking-widest uppercase mb-2" style={{ color: "#8080A0" }}>
              {t("landing.sim_you_win", "Tú habrías ganado")}
            </div>
            <div style={{
              fontFamily: "'Sora', sans-serif", fontWeight: 900,
              fontSize: "clamp(3rem, 13vw, 4.5rem)", lineHeight: 1, letterSpacing: "-0.04em",
              color: "#10B981",
              filter: "drop-shadow(0 4px 24px rgba(16,185,129,0.5))",
              transition: "all 0.2s ease",
            }} key={calc.profit}>
              ${fmt(calc.profit)}
            </div>
            <div className="text-xs mt-2 font-semibold" style={{ color: "#707090" }}>
              {t("landing.sim_net", "ganancia neta")} · {netUnits.toFixed(1)} {t("landing.sim_units", "unidades")}
            </div>
          </div>

          {/* Slider */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-bold" style={{ color: "#B0B0C8" }}>
                {t("landing.sim_per_pick", "Por jugada")}:
              </span>
              <span className="text-lg font-black" style={{ color: "#D4AF37", fontFamily: "'Sora', sans-serif" }}>
                ${betSize}
              </span>
            </div>
            <input
              type="range"
              min={5}
              max={500}
              step={5}
              value={betSize}
              onChange={(e) => setBetSize(Number(e.target.value))}
              data-testid="sim-slider"
              style={{
                width: "100%", height: 8, borderRadius: 999, outline: "none",
                background: `linear-gradient(90deg, #D4AF37 ${(betSize - 5) / 495 * 100}%, rgba(255,255,255,0.08) ${(betSize - 5) / 495 * 100}%)`,
                WebkitAppearance: "none", cursor: "pointer",
              }}
            />
            <div className="flex justify-between mt-3">
              {marks.map((m) => (
                <button
                  key={m}
                  onClick={() => setBetSize(m)}
                  className="text-[11px] font-black px-3 py-1.5 rounded-full transition-all"
                  style={betSize === m
                    ? { background: "rgba(212,175,55,0.18)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" }
                    : { background: "rgba(255,255,255,0.04)", color: "#707090", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  ${m}
                </button>
              ))}
            </div>
          </div>

          {/* Desglose */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: TrendUp, label: t("landing.sim_wagered", "Apostado"), value: `$${fmt(calc.wagered)}`, color: "#9090B0" },
              { icon: CurrencyDollar, label: t("landing.sim_profit", "Ganancia"), value: `+$${fmt(calc.profit)}`, color: "#10B981" },
              { icon: Percent, label: "ROI", value: `${calc.roi.toFixed(1)}%`, color: "#D4AF37" },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl p-3 text-center"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                <s.icon size={16} weight="bold" style={{ color: s.color, margin: "0 auto 6px" }} />
                <div className="text-sm font-black" style={{ color: s.color, fontFamily: "'Sora', sans-serif" }}>{s.value}</div>
                <div className="text-[10px] font-semibold mt-1" style={{ color: "#707090" }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div className="text-center mt-5 text-[11px]" style={{ color: "#60607A" }}>
            {t("landing.sim_disclaimer", "Basado en resultados reales documentados. Las apuestas conllevan riesgo.")}
          </div>
        </div>
      </div>

      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 28px; height: 28px; border-radius: 50%;
          background: radial-gradient(circle at 35% 30%, #F5D76E, #D4AF37 60%, #9A7B1E);
          border: 3px solid #0d0d14;
          box-shadow: 0 0 0 2px rgba(212,175,55,0.4), 0 4px 16px rgba(212,175,55,0.5);
          cursor: grab;
        }
        input[type="range"]::-moz-range-thumb {
          width: 24px; height: 24px; border-radius: 50%;
          background: radial-gradient(circle at 35% 30%, #F5D76E, #D4AF37 60%, #9A7B1E);
          border: 3px solid #0d0d14;
          box-shadow: 0 0 0 2px rgba(212,175,55,0.4), 0 4px 16px rgba(212,175,55,0.5);
          cursor: grab;
        }
      `}</style>
    </section>
  );
}
